import { Box, styled, SxProps, Theme, Typography } from '@mui/material';
import Checkbox from 'components/Checkbox';
import { MetadataDisplay } from 'components/MetadataDisplay';

const Base = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    overflow: 'hidden',
}));

interface BaseItemPickerProps<T> {
    items: T[];
    renderLabel: (item: T) => string;
    renderAdornment?: (item: T) => React.ReactNode;
    renderSubtitle?: (item: T) => string[];
    renderActions?: (item: T, disabled: boolean) => React.ReactNode;
    isItemDisabled?: (item: T) => boolean;
    compareFunc: (item: T, value: T | null) => boolean;
    sx?: SxProps<Theme>;
    itemSx?: SxProps<Theme>;
    disabled?: boolean;
}

export interface ItemPickerProps<T> extends BaseItemPickerProps<T> {
    picked?: T | null;
    onPick: (item: T) => void;
}

export interface MultiItemPickerProps<T> extends BaseItemPickerProps<T> {
    picked: T[];
    onPick: (items: T[]) => void;
}

export function ItemPicker<T>({
    picked = null,
    onPick,
    ...props
}: ItemPickerProps<T>): React.ReactElement | null {
    return (
        <SelectableItemPicker
            {...props}
            picked={picked ? [picked] : []}
            mode='single'
            onPick={nextPicked => {
                const next = nextPicked[0];

                if (!next) {
                    return;
                }

                onPick(next);
            }}
        />
    );
}

export function MultiItemPicker<T>({
    picked,
    onPick,
    ...props
}: MultiItemPickerProps<T>): React.ReactElement | null {
    return (
        <SelectableItemPicker
            {...props}
            picked={picked}
            mode='multiple'
            onPick={onPick}
        />
    );
}

interface SelectableItemPickerProps<T> extends BaseItemPickerProps<T> {
    picked: T[];
    mode: 'single' | 'multiple';
    onPick: (items: T[]) => void;
}

function SelectableItemPicker<T>({
    items,
    picked,
    mode,
    onPick,
    renderLabel,
    renderAdornment,
    renderSubtitle,
    renderActions,
    isItemDisabled,
    compareFunc,
    sx,
    itemSx,
    disabled,
}: SelectableItemPickerProps<T>): React.ReactElement | null {
    if (items.length === 0) {
        return null;
    }

    return (
        <Base sx={sx}>
            {items.map((item, i) => {
                const isSelected = picked.some(value =>
                    compareFunc(item, value)
                );
                const itemDisabled =
                    !!disabled || (isItemDisabled?.(item) ?? false);

                return (
                    <ItemComponent
                        key={i}
                        index={i}
                        label={renderLabel(item)}
                        adornment={renderAdornment?.(item)}
                        subtitle={renderSubtitle?.(item)}
                        actions={renderActions?.(item, itemDisabled)}
                        isSelected={isSelected}
                        selectionVariant={mode}
                        onPick={() => {
                            if (itemDisabled) {
                                return;
                            }

                            if (mode === 'single') {
                                onPick([item]);
                                return;
                            }

                            if (isSelected) {
                                onPick(
                                    picked.filter(
                                        value => !compareFunc(item, value)
                                    )
                                );
                                return;
                            }

                            onPick([...picked, item]);
                        }}
                        disabled={itemDisabled}
                        sx={itemSx}
                    />
                );
            })}
        </Base>
    );
}

const ItemRow = styled('div')<{ $disabled?: boolean; $index: number }>(
    ({ theme, $disabled, $index }) => ({
        'display': 'flex',
        'flexDirection': 'row',
        'alignItems': 'center',
        'justifyContent': 'space-between',
        'gap': theme.spacing(1.5),
        'padding': theme.spacing(1, 1.5),
        'cursor': $disabled ? 'not-allowed' : 'pointer',
        'userSelect': 'none',
        'transition': 'background-color 200ms ease-in-out',
        'opacity': $disabled ? 0.6 : 1,
        'backgroundColor':
            $index % 2 === 1 ? 'transparent' : theme.palette.grey[50],

        '&:hover': !$disabled && {
            backgroundColor: theme.palette.primary[50],
        },
    })
);

const Left = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(2),
}));

const Right = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
}));

const RadioButton = styled('div')<{ $selected: boolean }>(
    ({ theme, $selected }) => ({
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: `2px solid ${theme.palette.primary.main}`,
        backgroundColor: $selected ? theme.palette.primary.main : 'transparent',
        transition: 'all 200ms ease-in-out',
        flexShrink: 0,
    })
);

const TextContent = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
});

interface ItemComponentProps {
    label: string;
    adornment?: React.ReactNode;
    subtitle?: string[];
    actions?: React.ReactNode;
    isSelected: boolean;
    selectionVariant: 'single' | 'multiple';
    onPick: () => void;
    disabled?: boolean;
    sx?: SxProps<Theme>;
    index: number;
}

function ItemComponent({
    label,
    adornment,
    subtitle,
    actions,
    isSelected,
    selectionVariant,
    onPick,
    disabled,
    sx,
    index,
}: ItemComponentProps) {
    return (
        <ItemRow
            onClick={disabled ? undefined : onPick}
            $disabled={disabled}
            $index={index}
            sx={sx}>
            <Left>
                {selectionVariant === 'single' ? (
                    <RadioButton $selected={isSelected} />
                ) : (
                    <Checkbox
                        checked={isSelected}
                        disabled={disabled}
                        onChange={() => onPick()}
                        size='small'
                        sx={{ padding: 0 }}
                    />
                )}
                <TextContent>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            columnGap: 1,
                        }}>
                        <Typography variant='body2' color='textPrimary'>
                            {label}
                        </Typography>
                        {adornment}
                    </Box>
                    {subtitle && (
                        <MetadataDisplay
                            parts={subtitle}
                            variant='caption'
                            color='textSecondary'
                        />
                    )}
                </TextContent>
            </Left>
            {actions && (
                <Right onClick={e => e.stopPropagation()}>{actions}</Right>
            )}
        </ItemRow>
    );
}
